import { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Drawer,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Upload,
  message,
  Row,
  Col,
  Card,
  Space,
  Tag,
  Divider,
  Popconfirm,
  Image,
  Statistic,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  FilePdfOutlined,
  UploadOutlined,
  AccountBookOutlined,
  PrinterOutlined,
  FileExcelOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { contractAPI, userAPI, screenAPI } from '../services/api';
import { themeColors } from '../config/theme';
import { generateContractPdf } from '../utils/generateContractPdf';
import { generateReceiptPdf } from '../utils/generateReceiptPdf';
import { exportContractsExcel, exportContractsPdf } from '../utils/generateContractsReport';
import { DEFAULT_PRICING_TABLE } from '../data/contractTemplate';

const MEDIA_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api').replace(/\/api\/?$/, '');
const ROLE_CLIENT = 2;

const STATUS_COLORS = { draft: 'default', sent: 'blue', signed: 'green', cancelled: 'red' };

const DATE_FIELDS = ['contract_date', 'campaign_start_date', 'campaign_end_date', 'provider_signed_date', 'client_signed_date'];

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [clients, setClients] = useState([]);
  const [screens, setScreens] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [providerSigUploading, setProviderSigUploading] = useState(false);
  const [clientSigUploading, setClientSigUploading] = useState(false);
  const [form] = Form.useForm();
  const providerSignatureUrl = Form.useWatch('provider_signature_url', form);
  const clientSignatureUrl = Form.useWatch('client_signature_url', form);

  const [ledgerModal, setLedgerModal] = useState({ open: false, contract: null });
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentForm] = Form.useForm();
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, [pagination]);

  useEffect(() => {
    fetchClients();
    fetchScreens();
  }, []);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const response = await contractAPI.list({ page: pagination.page, limit: pagination.limit });
      setContracts(response.data.data.data);
      setTotal(response.data.data.pagination.total);
    } catch (error) {
      message.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await userAPI.list({ page: 1, limit: 200 });
      setClients(response.data.data.data.filter((u) => u.role_id === ROLE_CLIENT));
    } catch (error) {
      message.error('Failed to load clients');
    }
  };

  const fetchScreens = async () => {
    try {
      const response = await screenAPI.list({ page: 1, limit: 200 });
      setScreens(response.data.data.data);
    } catch (error) {
      message.error('Failed to load screens');
    }
  };

  const openCreateDrawer = () => {
    setEditingId(null);
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      contract_date: dayjs(),
      provider_company_name: 'CloudScreen / Cloudnet Softwares',
      provider_phone: '+66 94 871 2350',
      pricing_table: DEFAULT_PRICING_TABLE,
      total_advertising_fee: 0,
      vat_tax: 0,
      payment_received: 0,
    });
    setDrawerOpen(true);
  };

  const openEditDrawer = (record) => {
    setEditingId(record.id);
    setEditingRecord(record);
    const values = { ...record };
    DATE_FIELDS.forEach((f) => {
      values[f] = record[f] ? dayjs(record[f]) : null;
    });
    values.pricing_table = record.pricing_table && record.pricing_table.length ? record.pricing_table : DEFAULT_PRICING_TABLE;
    form.setFieldsValue(values);
    setDrawerOpen(true);
  };

  const handleClientChange = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) return;
    form.setFieldsValue({
      client_company_name: client.company_name || client.full_name,
      client_contact_person: client.full_name,
      client_address: client.company_address,
      client_phone: client.phone,
      client_email: client.email,
      client_tax_id: client.tax_id,
      client_rep_name: client.full_name,
    });
  };

  const handleScreenChange = (screenId) => {
    const screen = screens.find((s) => s.id === screenId);
    if (!screen) return;
    form.setFieldsValue({
      led_screen_location: `${screen.screen_name} (${screen.location || screen.screen_code})`,
      screen_resolution:
        screen.resolution_width && screen.resolution_height
          ? `${screen.resolution_width} x ${screen.resolution_height}`
          : undefined,
    });
  };

  const handleUploadSignature = async (file, fieldName, setUploading) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await contractAPI.uploadSignature(formData);
      form.setFieldsValue({ [fieldName]: response.data.data.url });
      message.success('Signature uploaded');
    } catch (error) {
      message.error(error.response?.data?.message || 'Signature upload failed');
    } finally {
      setUploading(false);
    }
    return false; // prevent antd Upload's own auto-upload
  };

  const handleSubmit = async (values) => {
    const payload = { ...values };
    DATE_FIELDS.forEach((f) => {
      payload[f] = values[f] ? values[f].format('YYYY-MM-DD') : null;
    });

    try {
      setSaving(true);
      if (editingId) {
        await contractAPI.update(editingId, payload);
        message.success('Contract updated');
      } else {
        await contractAPI.create(payload);
        message.success('Contract created');
      }
      setDrawerOpen(false);
      form.resetFields();
      setEditingId(null);
      setEditingRecord(null);
      fetchContracts();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save contract');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await contractAPI.delete(id);
      message.success('Contract deleted');
      fetchContracts();
    } catch (error) {
      message.error('Failed to delete contract');
    }
  };

  const handleGeneratePdf = async (record) => {
    setGeneratingId(record.id);
    try {
      await generateContractPdf(record);
    } catch (error) {
      message.error('Failed to generate PDF');
    } finally {
      setGeneratingId(null);
    }
  };

  const openLedger = async (record) => {
    setLedgerModal({ open: true, contract: record });
    setPaymentsLoading(true);
    paymentForm.resetFields();
    try {
      const response = await contractAPI.listPayments(record.id);
      setLedgerModal({ open: true, contract: response.data.data.contract });
      setPayments(response.data.data.payments);
    } catch (error) {
      message.error('Failed to load payment history');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleAddPayment = async (values) => {
    try {
      setPaymentSaving(true);
      const response = await contractAPI.addPayment(ledgerModal.contract.id, {
        amount: values.amount,
        paymentDate: values.paymentDate ? values.paymentDate.format('YYYY-MM-DD') : undefined,
        paymentMethod: values.paymentMethod,
        notes: values.notes,
      });
      message.success(`Payment recorded - receipt ${response.data.data.payment.receipt_no}`);
      setLedgerModal({ open: true, contract: response.data.data.contract });
      setPayments((prev) => [...prev, response.data.data.payment]);
      paymentForm.resetFields();
      fetchContracts();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to record payment');
    } finally {
      setPaymentSaving(false);
    }
  };

  const handlePrintReceipt = async (payment) => {
    try {
      await generateReceiptPdf(ledgerModal.contract, payment);
    } catch (error) {
      message.error('Failed to generate receipt PDF');
    }
  };

  // Reports export every contract, not just the current table page - the
  // list endpoint caps at 100 per page (backend PAGINATION.MAX_LIMIT), so
  // page through it fully rather than assuming everything fits in one call.
  const fetchAllContracts = async () => {
    const first = await contractAPI.list({ page: 1, limit: 100 });
    const all = [...first.data.data.data];
    const totalPages = first.data.data.pagination.totalPages;
    for (let page = 2; page <= totalPages; page += 1) {
      const response = await contractAPI.list({ page, limit: 100 });
      all.push(...response.data.data.data);
    }
    return all;
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const all = await fetchAllContracts();
      if (!all.length) {
        message.warning('No contracts to export');
        return;
      }
      exportContractsExcel(all);
    } catch (error) {
      message.error('Failed to export Excel report');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const all = await fetchAllContracts();
      if (!all.length) {
        message.warning('No contracts to export');
        return;
      }
      exportContractsPdf(all);
    } catch (error) {
      message.error('Failed to export PDF report');
    } finally {
      setExportingPdf(false);
    }
  };

  const columns = [
    { title: 'Agreement No.', dataIndex: 'agreement_no', key: 'agreement_no', width: 140 },
    { title: 'Client', dataIndex: 'client_company_name', key: 'client_company_name' },
    {
      title: 'Campaign',
      key: 'campaign',
      render: (_, record) =>
        record.campaign_start_date && record.campaign_end_date
          ? `${dayjs(record.campaign_start_date).format('DD MMM YYYY')} – ${dayjs(record.campaign_end_date).format('DD MMM YYYY')}`
          : 'N/A',
    },
    {
      title: 'Total Payable',
      dataIndex: 'total_payable',
      key: 'total_payable',
      render: (v) => `THB ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (v) => `THB ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{(status || 'draft').toUpperCase()}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 190,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<AccountBookOutlined />}
            onClick={() => openLedger(record)}
            title="Payment Ledger"
          />
          <Button
            type="primary"
            size="small"
            icon={<FilePdfOutlined />}
            loading={generatingId === record.id}
            onClick={() => handleGeneratePdf(record)}
            title="Generate PDF"
          />
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditDrawer(record)} />
          <Popconfirm title="Delete this contract?" onConfirm={() => handleDelete(record.id)}>
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDrawer}>
            New Contract
          </Button>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchContracts} loading={loading} />
        </Col>
        <Col>
          <Button icon={<FileExcelOutlined />} loading={exportingExcel} onClick={handleExportExcel}>
            Export Excel
          </Button>
        </Col>
        <Col>
          <Button icon={<ExportOutlined />} loading={exportingPdf} onClick={handleExportPdf}>
            Export PDF
          </Button>
        </Col>
      </Row>

      <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
        <Table
          columns={columns}
          dataSource={contracts}
          loading={loading}
          rowKey="id"
          scroll={{ x: true }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total,
            onChange: (page, pageSize) => setPagination({ page, limit: pageSize }),
          }}
        />
      </Card>

      <Drawer
        title={editingId ? 'Edit Contract' : 'New Contract'}
        open={drawerOpen}
        width={760}
        onClose={() => {
          setDrawerOpen(false);
          form.resetFields();
          setEditingId(null);
          setEditingRecord(null);
        }}
        extra={
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            {editingId ? 'Save Changes' : 'Create Contract'}
          </Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Divider orientation="left">Advertiser / Client</Divider>
          <Form.Item label="Select Client (autofills details below)">
            <Select
              placeholder="Select a client"
              showSearch
              optionFilterProp="label"
              options={clients.map((c) => ({
                value: c.id,
                label: `${c.company_name || c.full_name} (${c.email})`,
              }))}
              onChange={handleClientChange}
            />
          </Form.Item>
          <Form.Item name="client_id" hidden><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="client_company_name" label="Company / Business Name" rules={[{ required: true, message: 'Required' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="client_contact_person" label="Contact Person">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="client_address" label="Address">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="client_phone" label="Phone">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="client_email" label="Email">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="client_tax_id" label="Tax ID">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Provider</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="provider_company_name" label="Company Name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="provider_phone" label="Phone / WhatsApp">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="provider_address" label="Business Address">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="provider_email" label="Email">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Agreement</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contract_date" label="Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status" initialValue="draft">
                <Select
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'sent', label: 'Sent' },
                    { value: 'signed', label: 'Signed' },
                    { value: 'cancelled', label: 'Cancelled' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Campaign Details</Divider>
          <Form.Item label="Select Screen (autofills location/resolution below)">
            <Select
              allowClear
              placeholder="Select a screen"
              showSearch
              optionFilterProp="label"
              options={screens.map((s) => ({ value: s.id, label: `${s.screen_name} (${s.screen_code})` }))}
              onChange={handleScreenChange}
            />
          </Form.Item>
          <Form.Item name="screen_id" hidden><Input /></Form.Item>
          <Form.Item name="advertising_location" label="Advertising Location">
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="led_screen_location" label="LED Screen Location">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="screen_resolution" label="Screen Size / Resolution">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="campaign_start_date" label="Campaign Start Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="campaign_end_date" label="Campaign End Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="time_slot" label="Advertising Time Slot">
                <Input placeholder="e.g., 5:00 PM – 10:00 PM" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="max_ad_duration_seconds" label="Max Ad Duration Per Play (seconds)">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="rotation_notes" label="Expected Playback / Rotation">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="total_campaign_duration" label="Total Campaign Duration">
                <Input placeholder="e.g., 30 days" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="package_name" label="Advertising Package">
            <Input />
          </Form.Item>
          <Form.Item name="special_instructions" label="Special Instructions">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Divider orientation="left">Time-Slot Pricing (Section 5)</Divider>
          <Form.List name="pricing_table">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="middle">
                    <Col span={14}>
                      <Form.Item {...rest} name={[name, 'timeSlot']} rules={[{ required: true, message: 'Required' }]}>
                        <Input placeholder="Time slot" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item {...rest} name={[name, 'price']} rules={[{ required: true, message: 'Required' }]}>
                        <InputNumber min={0} style={{ width: '100%' }} addonBefore="THB" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ timeSlot: '', price: 0 })}>
                  Add Time Slot
                </Button>
              </>
            )}
          </Form.List>

          <Divider orientation="left">Payment (Section 44)</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="total_advertising_fee" label="Total Advertising Fee">
                <InputNumber min={0} style={{ width: '100%' }} addonBefore="THB" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="vat_tax" label="VAT / Tax">
                <InputNumber min={0} style={{ width: '100%' }} addonBefore="THB" />
              </Form.Item>
            </Col>
          </Row>
          {!editingId && (
            <Form.Item
              name="payment_received"
              label="Initial Payment Received (optional)"
              tooltip="If the client has already paid something (e.g. a deposit), enter it here - it's recorded as the first entry in this contract's payment ledger. Any payment after this is added from the Payment Ledger action on the contract list instead."
            >
              <InputNumber min={0} style={{ width: '100%' }} addonBefore="THB" />
            </Form.Item>
          )}
          {editingId && editingRecord && (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Statistic title="Payment Received" value={editingRecord.payment_received} prefix="THB" precision={2} />
              </Col>
              <Col span={8}>
                <Statistic title="Balance Due" value={editingRecord.balance} prefix="THB" precision={2} />
              </Col>
              <Col span={8}>
                <div style={{ color: themeColors.textSecondary, fontSize: 12, marginTop: 20 }}>
                  Tracked via this contract's Payment Ledger (ledger icon on the contract list) - not editable here.
                </div>
              </Col>
            </Row>
          )}

          <Divider orientation="left">Signatures (Section 45)</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="provider_rep_name" label="Provider Authorized Representative">
                <Input />
              </Form.Item>
              <Form.Item name="provider_rep_position" label="Position">
                <Input />
              </Form.Item>
              <Form.Item name="provider_signed_date" label="Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="provider_signature_url" label="Signature" hidden>
                <Input />
              </Form.Item>
              {providerSignatureUrl && (
                <Image src={`${MEDIA_BASE_URL}${providerSignatureUrl}`} height={50} style={{ marginBottom: 8 }} />
              )}
              <Upload
                showUploadList={false}
                beforeUpload={(file) => handleUploadSignature(file, 'provider_signature_url', setProviderSigUploading)}
              >
                <Button icon={<UploadOutlined />} loading={providerSigUploading}>
                  Upload Provider Signature
                </Button>
              </Upload>
            </Col>
            <Col span={12}>
              <Form.Item name="client_rep_name" label="Client Authorized Representative">
                <Input />
              </Form.Item>
              <Form.Item name="client_rep_position" label="Position">
                <Input />
              </Form.Item>
              <Form.Item name="client_signed_date" label="Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="client_signature_url" label="Signature" hidden>
                <Input />
              </Form.Item>
              {clientSignatureUrl && (
                <Image src={`${MEDIA_BASE_URL}${clientSignatureUrl}`} height={50} style={{ marginBottom: 8 }} />
              )}
              <Upload
                showUploadList={false}
                beforeUpload={(file) => handleUploadSignature(file, 'client_signature_url', setClientSigUploading)}
              >
                <Button icon={<UploadOutlined />} loading={clientSigUploading}>
                  Upload Client Signature
                </Button>
              </Upload>
            </Col>
          </Row>
        </Form>
      </Drawer>

      <Modal
        title={`Payment Ledger — ${ledgerModal.contract?.agreement_no || ''}`}
        open={ledgerModal.open}
        onCancel={() => setLedgerModal({ open: false, contract: null })}
        footer={null}
        width={700}
      >
        {ledgerModal.contract && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Statistic title="Total Payable" value={ledgerModal.contract.total_payable} prefix="THB" precision={2} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Received"
                  value={ledgerModal.contract.payment_received}
                  prefix="THB"
                  precision={2}
                  valueStyle={{ color: themeColors.success }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Balance Due"
                  value={ledgerModal.contract.balance}
                  prefix="THB"
                  precision={2}
                  valueStyle={{ color: Number(ledgerModal.contract.balance) > 0 ? themeColors.warning : themeColors.success }}
                />
              </Col>
            </Row>

            <Table
              size="small"
              rowKey="id"
              loading={paymentsLoading}
              dataSource={payments}
              pagination={false}
              locale={{ emptyText: <Empty description="No payments recorded yet" /> }}
              columns={[
                { title: 'Date', dataIndex: 'payment_date', key: 'payment_date', render: (v) => dayjs(v).format('DD MMM YYYY') },
                { title: 'Receipt No.', dataIndex: 'receipt_no', key: 'receipt_no' },
                { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v) => `THB ${Number(v).toLocaleString()}` },
                { title: 'Method', dataIndex: 'payment_method', key: 'payment_method', render: (v) => v || '—' },
                { title: 'Received By', dataIndex: 'received_by_name', key: 'received_by_name', render: (v) => v || '—' },
                {
                  title: '',
                  key: 'print',
                  width: 40,
                  render: (_, payment) => (
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      title="Print Receipt"
                      onClick={() => handlePrintReceipt(payment)}
                    />
                  ),
                },
              ]}
              style={{ marginBottom: 16 }}
            />

            <Divider orientation="left" style={{ fontSize: 13 }}>Record New Payment</Divider>
            <Form form={paymentForm} layout="inline" onFinish={handleAddPayment}>
              <Form.Item name="amount" rules={[{ required: true, message: 'Amount required' }]}>
                <InputNumber min={0.01} placeholder="Amount" addonBefore="THB" style={{ width: 160 }} />
              </Form.Item>
              <Form.Item name="paymentDate" initialValue={dayjs()}>
                <DatePicker placeholder="Date" />
              </Form.Item>
              <Form.Item name="paymentMethod">
                <Select
                  placeholder="Method"
                  style={{ width: 140 }}
                  allowClear
                  options={[
                    { value: 'Cash', label: 'Cash' },
                    { value: 'Bank Transfer', label: 'Bank Transfer' },
                    { value: 'Cheque', label: 'Cheque' },
                    { value: 'Other', label: 'Other' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="notes" style={{ flex: 1, minWidth: 160 }}>
                <Input placeholder="Notes (optional)" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={paymentSaving}>
                  Add Payment
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
}
